import React, { useEffect, useRef, useState } from "react";
import {
  FiCheckCircle,
  FiCircle,
  FiDownload,
  FiExternalLink,
  FiImage,
  FiPlay,
  FiSend,
  FiUpload,
} from "react-icons/fi";
import { FormattedDate, FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { get, post } from "../../../api";
import BackButton from "../../cmp/BackButton";
import CopyButton from "../../cmp/CopyButton";

export interface CampaignType {
  id: number;
  name: string;
  brief?: string;
  affiliate_id?: string;
  campaign_key: string;
  affiliate: {
    name: string;
    id: string;
    ref_code: string;
    email: string;
    profile_photo?: {
      url: string;
    };
  };
  links: Array<{ id: string; title: string; sub_id: string; url: string }>;
  deliverables: Array<{
    id: string;
    title: string;
    completed_at?: string; // ISO 8601
    admin_note?: string;
  }>;
  contract_template_url?: string;
  contract_signed_url?: string;
  // contract_notes?: string;
  // contract?: string;
  // is_contract_signed?: boolean;
  milestones: Array<{
    id: string;
    title: string;
    due_date?: string; // ISO 8601
    payout?: string;
    completed_at?: string; // ISO 8601
    admin_note?: string;
    status?: "completed" | "pending" | "overdue" | "under_review" | "cancelled";
  }>;
  start_date?: string; // ISO 8601
  end_date?: string; // ISO 8601
  budget?: number;
  cogs?: number; // cost of goods as percentage to calculate ROI
  fixed_fee?: number;
  gift_ids: string | null; // csv of gift ids
  admin_note?: string;
  affiliate_note?: string;
}

interface CampaignAsset {
  id: string;
  title: string;
  type: "image" | "video";
  url: string;
  thumbnail?: string;
}

interface ChatMessage {
  id: string;
  text: string;
  sender: "affiliate" | "brand";
  sent_at: string;
}

function CampaignAssetsCard({ campaign_key }: { campaign_key: string }) {
  const { data: assetsData } = useSWR<{ assets: CampaignAsset[] }>(
    "/campaigns/assets?campaign_key=" + campaign_key,
    get,
  );
  const assets = assetsData?.assets;
  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleSubmit = () => {
    if (!files.length) return;
    setSubmitting(true);
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    if (note) formData.append("note", note);
    post(`/campaigns/content?campaign_key=${campaign_key}`, formData)
      .then(() => {
        setSubmitted(true);
        setFiles([]);
        setNote("");
        if (fileInputRef.current) fileInputRef.current.value = "";
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="card mb-3">
      <div className="card-header fw-semibold">
        <FormattedMessage id="Campaign Assets" />
      </div>

      {/* Brand-provided assets */}
      {assets && assets.length > 0 && (
        <div className="card-body border-bottom pb-3">
          <p
            className="text-muted small mb-2 fw-semibold text-uppercase"
            style={{ letterSpacing: "0.05em" }}
          >
            <FormattedMessage id="Brand Assets" />
          </p>
          <div className="row g-2">
            {assets.map((asset) => (
              <div key={asset.id} className="col-6 col-sm-3">
                <a
                  href={asset.url}
                  target="_blank"
                  rel="noreferrer"
                  className="d-block text-decoration-none"
                >
                  <div
                    className="position-relative rounded overflow-hidden border bg-light"
                    style={{ aspectRatio: "4/3" }}
                  >
                    {asset.thumbnail ? (
                      <img
                        src={asset.thumbnail}
                        alt={asset.title}
                        className="w-100 h-100"
                        style={{ objectFit: "cover" }}
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                        <FiImage size={28} />
                      </div>
                    )}
                    {asset.type === "video" && (
                      <div
                        className="position-absolute top-50 start-50 translate-middle bg-dark bg-opacity-50 rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: 36, height: 36 }}
                      >
                        <FiPlay size={16} className="text-white" />
                      </div>
                    )}
                    <div className="position-absolute bottom-0 end-0 p-1">
                      <span className="badge bg-dark bg-opacity-60 d-inline-flex align-items-center gap-1">
                        <FiDownload size={10} />
                      </span>
                    </div>
                  </div>
                  <div className="mt-1 small text-truncate text-body">
                    {asset.title}
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content submission */}
      <div className="card-body">
        <p
          className="text-muted small mb-2 fw-semibold text-uppercase"
          style={{ letterSpacing: "0.05em" }}
        >
          <FormattedMessage id="Submit Your Content" />
        </p>
        {submitted && (
          <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-2">
            <FiCheckCircle size={16} />
            <span>
              <FormattedMessage id="Content submitted successfully!" />
            </span>
          </div>
        )}
        <div className="mb-2">
          <input
            type="file"
            ref={fileInputRef}
            className="d-none"
            multiple
            accept="image/*,video/*"
            onChange={handleFileChange}
          />
          <button
            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
            onClick={() => fileInputRef.current?.click()}
          >
            <FiUpload size={13} />
            <FormattedMessage id="Choose Files" />
          </button>
          {files.length > 0 && (
            <span className="ms-2 small text-muted">
              {files.length} file{files.length > 1 ? "s" : ""} selected
            </span>
          )}
        </div>
        <textarea
          className="form-control form-control-sm mb-2"
          rows={2}
          placeholder="Add a note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={{ resize: "none" }}
        />
        <button
          className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
          onClick={handleSubmit}
          disabled={submitting || !files.length}
        >
          <FiSend size={13} />
          <FormattedMessage id="Submit Content" />
        </button>
      </div>
    </div>
  );
}

function CampaignChat({ campaign_key }: { campaign_key: string }) {
  const { data: messagesData, mutate } = useSWR<{ messages: ChatMessage[] }>(
    `/campaigns/messages?campaign_key=${campaign_key}`,
    get,
  );
  const messages = messagesData?.messages ?? [];
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const optimistic: ChatMessage = {
      id: `tmp_${Date.now()}`,
      sender: "affiliate",
      text: trimmed,
      sent_at: new Date().toISOString(),
    };
    mutate(
      (current) => ({ messages: [...(current?.messages ?? []), optimistic] }),
      { revalidate: false },
    );
    setText("");
    setSending(true);
    post(`/campaigns/messages?campaign_key=${campaign_key}`, {
      message: trimmed,
    })
      .then(() => mutate())
      .finally(() => setSending(false));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-header fw-semibold">
        <FormattedMessage id="Messages" />
      </div>
      <div
        className="card-body d-flex flex-column gap-2"
        style={{ maxHeight: 360, minHeight: 360, overflowY: "auto" }}
      >
        {messages.length === 0 && (
          <div className="text-center text-muted py-4 my-auto">
            <FormattedMessage id="No messages yet. Start the conversation!" />
          </div>
        )}
        {messages.map((msg) => {
          const isAffiliate = msg.sender === "affiliate";
          return (
            <div
              key={msg.id}
              className={`d-flex flex-column ${isAffiliate ? "align-items-end" : "align-items-start"}`}
            >
              <div
                style={{
                  maxWidth: "78%",
                  backgroundColor: isAffiliate ? "#0d6efd" : "#f1f3f5",
                  color: isAffiliate ? "#fff" : "#212529",
                  borderRadius: isAffiliate
                    ? "16px 16px 4px 16px"
                    : "16px 16px 16px 4px",
                  padding: "8px 12px",
                  fontSize: 13,
                  lineHeight: 1.45,
                  wordBreak: "break-word",
                }}
              >
                {msg.text}
              </div>
              <small className="text-muted mt-1" style={{ fontSize: 11 }}>
                <FormattedDate
                  value={msg.sent_at}
                  month="short"
                  day="numeric"
                  hour="numeric"
                  minute="2-digit"
                />
              </small>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div className="card-footer p-2">
        <div className="d-flex gap-2 align-items-end">
          <textarea
            className="form-control form-control-sm"
            rows={2}
            placeholder="Type a message… (Enter to send)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ resize: "none" }}
          />
          <button
            className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
            onClick={handleSend}
            disabled={sending || !text.trim()}
            style={{ whiteSpace: "nowrap" }}
          >
            <FiSend size={14} />
            <FormattedMessage id="Send" />
          </button>
        </div>
      </div>
    </div>
  );
}

function MilestoneStatusBadge({
  status,
}: {
  status?: CampaignType["milestones"][number]["status"];
}) {
  switch (status) {
    case "completed":
      return (
        <span className="badge badge-success">
          <FormattedMessage id="Completed" />
        </span>
      );
    case "overdue":
      return (
        <span className="badge badge-danger">
          <FormattedMessage id="Overdue" />
        </span>
      );
    case "under_review":
      return (
        <span className="badge badge-warning">
          <FormattedMessage id="Under Review" />
        </span>
      );
    case "cancelled":
      return (
        <span className="badge badge-secondary">
          <FormattedMessage id="Cancelled" />
        </span>
      );
    case "pending":
    default:
      return (
        <span className="badge badge-warning">
          <FormattedMessage id="Pending" />
        </span>
      );
  }
}

export default function CampaignIndex() {
  const { campaign_key } = useParams();
  const { data, isLoading } = useSWR<{ campaign: CampaignType }>(
    `/campaigns?campaign_key=${campaign_key}`,
    get,
  );

  if (isLoading) {
    return (
      <div className="container py-4">
        <div className="mb-3">
          <BackButton />
        </div>
        <div className="text-center text-muted py-5">Loading…</div>
      </div>
    );
  }

  if (!data?.campaign) {
    return (
      <div className="container py-4">
        <div className="mb-3">
          <BackButton />
        </div>
        <div className="alert alert-warning">Campaign not found.</div>
      </div>
    );
  }

  const campaign = data.campaign;

  const {
    name,
    brief,
    start_date,
    end_date,
    affiliate_note,
    links,
    deliverables,
    milestones,
    contract_template_url,
    contract_signed_url,
  } = campaign;

  return (
    <div className="container py-4">
      <div className="mb-3">
        <BackButton />
      </div>

      {/* Header */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div className="flex-grow-1 min-w-0">
              <h2 className="mb-0">{name}</h2>
              {(start_date || end_date) && (
                <p className="text-muted mb-0 mt-1" style={{ fontSize: 14 }}>
                  {start_date && (
                    <FormattedDate
                      value={start_date}
                      year="numeric"
                      month="short"
                      day="numeric"
                    />
                  )}
                  {start_date && end_date && " – "}
                  {end_date && (
                    <FormattedDate
                      value={end_date}
                      year="numeric"
                      month="short"
                      day="numeric"
                    />
                  )}
                </p>
              )}
            </div>
            <div className="d-flex flex-wrap gap-2 flex-shrink-0">
              {contract_template_url && (
                <a
                  href={contract_template_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                >
                  <FiExternalLink size={14} />
                  <FormattedMessage id="View Contract" />
                </a>
              )}
              {contract_signed_url && (
                <a
                  href={contract_signed_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1"
                >
                  <FiCheckCircle size={14} />
                  <FormattedMessage id="View Signed Contract" />
                </a>
              )}
            </div>
          </div>
          {brief && (
            <p className="mb-0 mt-3 text-secondary border-top pt-3">{brief}</p>
          )}
        </div>
      </div>

      {/* Affiliate Note */}
      {affiliate_note && (
        <div className="alert alert-info mb-3">{affiliate_note}</div>
      )}

      {/* Two-column layout */}
      <div className="row g-3 align-items-start">
        {/* Main column: Deliverables + Milestones */}
        <div className="col-lg-7">
          {/* Deliverables */}
          {deliverables?.length > 0 && (
            <div className="card mb-3">
              <div className="card-header fw-semibold">
                <FormattedMessage id="Deliverables" />
                <span className="ms-2 badge bg-secondary">
                  {deliverables.filter((d) => d.completed_at).length}/
                  {deliverables.length}
                </span>
              </div>
              <ul className="list-group list-group-flush">
                {deliverables.map((item) => (
                  <li
                    key={item.id}
                    className="list-group-item d-flex align-items-start gap-2"
                  >
                    <span
                      className={
                        item.completed_at ? "text-success" : "text-muted"
                      }
                      style={{ marginTop: 2 }}
                    >
                      {item.completed_at ? (
                        <FiCheckCircle size={16} />
                      ) : (
                        <FiCircle size={16} />
                      )}
                    </span>
                    <div>
                      <span
                        className={
                          item.completed_at
                            ? "text-decoration-line-through text-muted"
                            : ""
                        }
                      >
                        {item.title}
                      </span>
                      {item.completed_at && (
                        <div>
                          <small className="text-muted">
                            <FormattedMessage id="Completed" />{" "}
                            <FormattedDate
                              value={item.completed_at}
                              year="numeric"
                              month="short"
                              day="numeric"
                            />
                          </small>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Campaign Assets & Content Submission */}
          <CampaignAssetsCard campaign_key={String(campaign_key || "")} />

          {/* Milestones */}
          {milestones?.length > 0 && (
            <div className="card mb-3">
              <div className="card-header fw-semibold">
                <FormattedMessage id="Milestones" />
              </div>
              <ul className="list-group list-group-flush">
                {milestones.map((milestone) => (
                  <li key={milestone.id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <div className="fw-semibold">{milestone.title}</div>
                        <div className="d-flex flex-wrap gap-3 mt-1">
                          {milestone.due_date && (
                            <small className="text-muted">
                              <FormattedMessage id="Due" />:{" "}
                              <FormattedDate
                                value={milestone.due_date}
                                year="numeric"
                                month="short"
                                day="numeric"
                              />
                            </small>
                          )}
                          {milestone.payout && (
                            <small className="text-muted fw-semibold">
                              <FormattedMessage id="Payout" />:{" "}
                              {milestone.payout}
                            </small>
                          )}
                        </div>
                      </div>
                      <MilestoneStatusBadge status={milestone.status} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sidebar: Tracking Links + Chat */}
        <div className="col-lg-5">
          {/* Tracking Links */}
          {links?.length > 0 && (
            <div className="card mb-3">
              <div className="card-header fw-semibold">
                <FormattedMessage id="Tracking Links" />
              </div>
              <ul className="list-group list-group-flush">
                {links.map((link) => (
                  <li key={link.id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-center gap-2">
                      <div className="overflow-hidden">
                        <div className="fw-semibold">{link.title}</div>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noreferrer"
                          className="small text-break"
                        >
                          {link.url}
                        </a>
                        {link.sub_id && (
                          <div>
                            <small className="text-muted">
                              <FormattedMessage id="Sub ID" />: {link.sub_id}
                            </small>
                          </div>
                        )}
                      </div>
                      <CopyButton
                        text={link.url}
                        className="btn-link p-0"
                        style={undefined}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <CampaignChat campaign_key={campaign_key!} />
        </div>
      </div>
    </div>
  );
}
